import { useId } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import Button from './Button'
import InfoHint from './InfoHint'
import useDialog from './useDialog'
import { kit } from './strings'
import './Drawer.css'
import './neutral.css'

/**
 * The side sheet — the app's one overlay surface for anything with a form in it.
 *
 * Editing used to happen in a centred modal that covered the very list you were
 * editing from. This docks at the reading-start edge instead, so it opens where
 * the eye already is and leaves the list readable behind it. ConfirmDialog and
 * ResultDialog stay centred: those are the two cases where covering the page is
 * the point.
 *
 * @param {'sm'|'md'|'lg'|'xl'|'full'} [size]
 * @param {import('react').ElementType} [icon]  emblem glyph for the head
 * @param {string} [hint]  long-form guidance, folded behind an "i" beside the title
 * @param {import('react').ReactNode} [footer]  action strip pinned to the bottom
 * @param {boolean} [neutral]  plain-grey frame for the dialogs that list users — see neutral.css
 */
export default function Drawer({
  open,
  /* Centred-and-high rather than docked at the edge — see .drawer--dialog. */
  dialog = false,
  neutral = false,
  title,
  subtitle,
  hint,
  icon: Icon,
  size = 'md',
  onClose,
  footer,
  children,
}) {
  const titleId = useId()

  /* Scroll lock, Escape, the Tab trap, focus restore and the exit transition —
     all of it shared, so this sheet behaves exactly like every other dialog. */
  const { mounted, state, ref: panelRef } = useDialog({ open, onClose })

  if (!mounted) return null

  return createPortal(
    <div
      className={`drawer${dialog ? ' drawer--dialog' : ''}${neutral ? ' neutral-ui' : ''}${state === 'leaving' ? ' is-leaving' : ''}`}
      role="presentation"
      onClick={onClose}
      /* On the way out it is a PICTURE of a sheet, not a sheet: nothing in it
         should take a click or a screen reader's attention. */
      {...(state === 'leaving' ? { inert: true, 'aria-hidden': 'true' } : null)}
    >
      <div
        ref={panelRef}
        className={`drawer__panel drawer__panel--${size}`}
        data-testid="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="drawer__head">
          {Icon && (
            <span className="drawer__emblem" aria-hidden="true">
              <Icon size={19} strokeWidth={1.75} />
            </span>
          )}

          <div className="drawer__titles">
            <h2 className="drawer__title" id={titleId}>
              <bdi className="drawer__title-text">{title}</bdi>
              {hint && <InfoHint content={hint} />}
            </h2>
            {subtitle && (
              <p className="drawer__subtitle">
                <bdi>{subtitle}</bdi>
              </p>
            )}
          </div>

          <button type="button" className="drawer__close" data-testid="drawer-close" onClick={onClose} aria-label={kit.close}>
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className="drawer__body">{children}</div>

        {footer && <footer className="drawer__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
}

/**
 * The same sheet wrapped in a form, with the submit/cancel pair in the footer —
 * the shape every edit screen in the panel needs.
 */
/**
 * A form in a dialog.
 *
 * Centred by default, not docked. These were side sheets throughout, and a form
 * pinned to one edge of a wide screen reads as attached to the browser rather
 * than to the page — you look for it where you left the row, and it is not
 * there. `dock` puts one back on the edge for the rare sheet that is genuinely
 * worked ALONGSIDE the list behind it.
 *
 * Anything with steps, or with more fields than fit two comfortable columns,
 * belongs on a FormPage instead.
 */
/* `submitDisabled` — nothing to submit yet. Distinct from `submitting`, which draws the busy
   spinner: a form waiting for input is not a form waiting for the server. */
export function FormDrawer({ onSubmit, submitLabel, cancelLabel, submitting, submitDisabled = false, footer, dock = false, ...rest }) {
  return (
    <Drawer
      dialog={!dock}
      {...rest}
      footer={footer ?? (
        <>
          <Button type="submit" form={rest.formId} busy={submitting} disabled={submitDisabled}>
            {submitLabel}
          </Button>
          <Button variant="ghost" onClick={rest.onClose}>
            {cancelLabel}
          </Button>
        </>
      )}
    >
      <form id={rest.formId} onSubmit={onSubmit}>
        {rest.children}
      </form>
    </Drawer>
  )
}
